import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_CALLS } from '../../../lib/graphql/queries/contacts';
import type { GQLCallItem } from './useContact';

const PAGE_SIZE = 20;

interface GetContactCallsData {
  contact: {
    contactId: string;
    calls: {
      items: GQLCallItem[];
      total: number;
      hasMore: boolean;
    };
  } | null;
}

export const useContactCallsGQL = (
  contactId: string,
  dateRange?: { from: Date | null; to: Date | null }
) => {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error, refetch } = useQuery<GetContactCallsData>(GET_CONTACT_CALLS, {
    variables: {
      organizationId: organization?.id ?? '',
      contactId,
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      ...(dateRange?.from && { dateFrom: dateRange.from.toISOString() }),
      ...(dateRange?.to && { dateTo: dateRange.to.toISOString() }),
      sortBy: 'createdAt',
      sortOrder: 'DESC',
    },
    skip: !organization?.id || !contactId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const calls = data?.contact?.calls?.items ?? [];
  const total = data?.contact?.calls?.total ?? 0;
  const hasMore = data?.contact?.calls?.hasMore ?? false;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleNextPage = useCallback(() => {
    setCurrentPage((p) => p + 1);
  }, []);

  const handlePrevPage = useCallback(() => {
    setCurrentPage((p) => Math.max(0, p - 1));
  }, []);

  const goToPage = useCallback((page: number) => setCurrentPage(Math.max(0, page)), []);
  const resetPage = useCallback(() => setCurrentPage(0), []);

  return {
    calls,
    total,
    hasMore,
    loading,
    error,
    currentPage,
    totalPages,
    pageSize: PAGE_SIZE,
    hasNext: hasMore || currentPage < totalPages - 1,
    hasPrev: currentPage > 0,
    handleNextPage,
    handlePrevPage,
    goToPage,
    resetPage,
    refetch,
  };
};
